// toolDetailHelpers.js
//
// Shared constants + tiny utilities used across the tool detail page and its
// presentational subcomponents (examples panel, recent runs strip, empty
// result placeholder, etc).

import { heroChromaForSlug, paletteForSlug } from '@/utils/agent_catalog';
import { isModelLockedForUser } from '@/constants/utils/trial';

// ---------------------------------------------------------------------------
// Category palette
//
// All catalog surfaces (tools, agents, tasks, decks, models) hash through
// `colorForSlug` in `agent_catalog.js`. Known tool categories live in the
// `KNOWN_CATEGORY_COLORS` override there, so the tool hero and any other
// surface that hashes the same slug agree on the same hex.
// ---------------------------------------------------------------------------

export function getPaletteForTool(tool) {
  const slug = tool?.category?.slug
    || (typeof tool?.category === 'string' ? tool.category : null)
    || '';
  return paletteForSlug(slug);
}

/** Same category → palette mapping as getPaletteForTool, without a tool object. */
export function getPaletteForCategorySlug(slug) {
  return paletteForSlug(slug);
}

function rgbTupleFromPaletteFg(fg) {
  const s = String(fg || '');
  const m = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return null;
  return [+m[1], +m[2], +m[3]];
}

function rgbTupleToHex(r, g, b) {
  const x = (n) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, '0');
  return `#${x(r)}${x(g)}${x(b)}`;
}

function rgbaFromRgb(r, g, b, alpha) {
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Solid `#rrggbb` from a category `palette.fg` rgba string (for CTA / hashing). */
export function accentHexFromPaletteFg(fg) {
  const t = rgbTupleFromPaletteFg(fg);
  if (!t) return null;
  return rgbTupleToHex(t[0], t[1], t[2]);
}

/** Hero wash rgba stops from `palette.fg` (tool / deck copy that passes a palette). */
export function heroChromaFromPaletteFg(fg) {
  const t = rgbTupleFromPaletteFg(fg);
  if (!t) return heroChromaForSlug('_');
  const [r, g, b] = t;
  return {
    gradSoft: rgbaFromRgb(r, g, b, 0.09),
    gradMid: rgbaFromRgb(r, g, b, 0.045),
    blobA: rgbaFromRgb(r, g, b, 0.065),
    blobB: rgbaFromRgb(r, g, b, 0.04),
  };
}

/** Opaque hover fill for breadcrumbs — same string as `palette.fg`. */
export function hoverSurfaceFromPaletteFg(fg) {
  const s = String(fg || '').trim();
  return s || paletteForSlug('').fg;
}

// ---------------------------------------------------------------------------
// Output type metadata — used for icons, empty-state hints, and labels.
// ---------------------------------------------------------------------------
export const OUTPUT_TYPE_ICON = {
  text:       'align-left',
  image:      'image',
  video:      'video',
  audio:      'volume',
  music:      'music',
  code:       'code',
  document:   'file-lines',
  '3d-model': 'cube',
};

export const OUTPUT_TYPE_RESULT_HINT = {
  text:       'Your generated text will appear here.',
  image:      'Your generated image will appear here.',
  video:      'Your generated video will appear here.',
  audio:      'Your generated audio clip will appear here.',
  music:      'Your generated music will appear here.',
  code:       'Your generated code will appear here.',
  document:   'Your generated document will appear here.',
  '3d-model': 'Your generated 3D model will appear here.',
};

// ---------------------------------------------------------------------------
// Run status visuals — colored chips on run cards / progress UI.
// `t` is the translation function; passed in (instead of imported) so this
// module stays a pure helper and can be called from any context.
// ---------------------------------------------------------------------------
export function getRunStatusVisual(status, colors, palette, t) {
  const tx = (key, fallback) => (t ? (t(key) || fallback) : fallback);
  switch (status) {
    case 'completed':
      // Neutral, not the tool's accent — the accent on a check icon was reading
      // as a warning/CTA. Subtle secondary text colour keeps it as metadata.
      return { icon: 'circle-check',       label: tx('pages.tools.statusCompleted', 'Completed'), color: colors.text_color_secondary };
    case 'failed':
      return { icon: 'circle-exclamation', label: tx('pages.tools.statusFailed',    'Failed'),   color: colors.color_destructive };
    case 'cancelled':
      return { icon: 'ban',                label: tx('pages.tools.statusCancelled', 'Cancelled'), color: colors.text_color_tertiary };
    case 'running':
      return { icon: 'spinner',            label: tx('pages.tools.statusRunning',   'Running'),  color: colors.text_color_secondary };
    case 'pending':
      return { icon: 'clock',              label: tx('pages.tools.statusQueued',    'Queued'),   color: colors.text_color_secondary };
    default:
      return { icon: 'circle',             label: status || 'Unknown', color: colors.text_color_tertiary };
  }
}

// Resolve the empty-result hint copy for a given tool output type.
export function getResultHintKey(outputType) {
  switch (outputType) {
    case 'text':       return 'pages.tools.resultHintText';
    case 'image':      return 'pages.tools.resultHintImage';
    case 'video':      return 'pages.tools.resultHintVideo';
    case 'audio':      return 'pages.tools.resultHintAudio';
    case 'music':      return 'pages.tools.resultHintMusic';
    case 'code':       return 'pages.tools.resultHintCode';
    case 'document':   return 'pages.tools.resultHintDocument';
    case '3d-model':   return 'pages.tools.resultHint3dModel';
    default:           return 'pages.tools.resultHintDefault';
  }
}

// ---------------------------------------------------------------------------
// Tiny utilities
// ---------------------------------------------------------------------------

export function formatTimeAgo(date) {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60)            return `${Math.floor(diff)}s ago`;
  if (diff < 60 * 60)       return `${Math.floor(diff / 60)}m ago`;
  if (diff < 60 * 60 * 24)  return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function truncate(value, max = 90) {
  if (value == null) return '';
  const str = Array.isArray(value)
    ? value.map(v => (v && typeof v === 'object' ? (v.name || v.id || JSON.stringify(v)) : String(v))).join(', ')
    : (typeof value === 'object'
      ? (value.name || value.id || JSON.stringify(value))
      : String(value));
  if (str.length <= max) return str;
  return `${str.slice(0, max - 1)}…`;
}

// Pick the field that's most likely to summarize a preset/example.
//
// Prefer a textual field (the user's prompt / instruction / description) over
// non-string content like file uploads or selects. Otherwise tools whose
// first form field is a file upload (e.g. the AI Image Editor: source image
// is field 1, edit instruction is field 2) end up showing the file's raw
// name as the preset headline (`portrait-original.png`) instead of the
// prompt that actually distinguishes one preset from another.
//
// Order of preference:
//   1) First non-empty *string* field, in form order.
//   2) First non-empty field of any kind, in form order (handles tools that
//      have no text fields at all — image-only edits, selects, etc).
//   3) First entry on the example object as a last resort.
export function pickHeadlineField(example, visibleFields) {
  if (!example || typeof example !== 'object') return null;
  const fieldOrder = (visibleFields || []).map(f => f?.name).filter(Boolean);

  const isUsefulText = (v) => (
    typeof v === 'string' && v.trim().length > 0
  );

  for (const name of fieldOrder) {
    if (isUsefulText(example[name])) return { name, value: example[name] };
  }
  for (const name of fieldOrder) {
    const value = example[name];
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    return { name, value };
  }
  const entries = Object.entries(example);
  if (!entries.length) return null;
  const [name, value] = entries[0];
  return { name, value };
}

// Best-effort URL extractor for the file objects attached to a ToolRun.
export function getFileUrl(file) {
  if (!file || typeof file !== 'object') return null;
  return file.url || file.signedUrl || file.cdnPath || file.path || null;
}

/** Parse ratio strings like "9:16" or "16:9" into a numeric width/height ratio. */
export function parseAspectRatioString(value) {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)$/);
  if (!match) return null;
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!width || !height) return null;
  return width / height;
}

function clampPreviewAspectRatio(ratio) {
  if (!Number.isFinite(ratio) || ratio <= 0) return 1;
  return Math.min(Math.max(ratio, 0.35), 3);
}

/**
 * Resolve the aspect ratio to use when previewing a tool-run image thumbnail.
 * Prefers stored file metadata, then the run's aspectRatio form input.
 */
export function resolveToolImagePreviewAspectRatio(file, run) {
  const imgMeta = file?.metadata?.image;
  if (typeof imgMeta?.aspectRatio === 'number' && imgMeta.aspectRatio > 0) {
    return clampPreviewAspectRatio(imgMeta.aspectRatio);
  }
  if (imgMeta?.width && imgMeta?.height) {
    return clampPreviewAspectRatio(imgMeta.width / imgMeta.height);
  }
  const fromInput = parseAspectRatioString(run?.inputs?.aspectRatio);
  if (fromInput) return clampPreviewAspectRatio(fromInput);
  return 1;
}

/** Aspect ratio for tool history thumbnails (images + videos). */
export function resolveToolMediaPreviewAspectRatio(file, run, outputType) {
  const mime = file?.mimeType || '';
  const isVideo = outputType === 'video' || mime.startsWith('video/');
  if (isVideo) {
    const videoMeta = file?.metadata?.video;
    if (typeof videoMeta?.aspectRatio === 'number' && videoMeta.aspectRatio > 0) {
      return clampPreviewAspectRatio(videoMeta.aspectRatio);
    }
    if (videoMeta?.width && videoMeta?.height) {
      return clampPreviewAspectRatio(videoMeta.width / videoMeta.height);
    }
    const fromInput = parseAspectRatioString(run?.inputs?.aspectRatio);
    if (fromInput) return clampPreviewAspectRatio(fromInput);
    return 16 / 9;
  }
  return resolveToolImagePreviewAspectRatio(file, run);
}

// Walk a run's `inputs` and produce a short, scannable summary of what the
// user actually fed the tool. Lets the recent-runs strip / dropdown / history
// rows distinguish runs by content rather than just relative time when the
// user has many runs of the same tool.
//
// Rules:
//   - Skip booleans, null/empty, and file-shaped objects.
//   - Prefer string + number primitives.
//   - Take at most the first 2 values, joined with " · ".
//   - Fall back to the run's resultText if no usable inputs.
//   - DO NOT fall back to compiledPrompt — that's the full system+user
//     payload (template-substituted), and on tool runs it's almost always
//     dominated by boilerplate that's identical across runs (the literal
//     template wrapper). Showing it makes every card look the same.
export function summarizeRunInputs(run, maxChars = 90) {
  const inputs = run?.inputs;
  const parts = [];
  if (inputs && typeof inputs === 'object' && !Array.isArray(inputs)) {
    for (const value of Object.values(inputs)) {
      if (value == null || value === '') continue;
      if (typeof value === 'boolean') continue;
      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          const flat = value.filter(v => v != null && typeof v !== 'object').map(String);
          if (flat.length) parts.push(flat.join(', '));
        }
        continue;
      }
      parts.push(String(value));
      if (parts.length >= 2) break;
    }
  }

  let joined = parts.join(' \u00b7 ').replace(/\s+/g, ' ').trim();
  if (!joined && Array.isArray(run?.resultSegments) && run.resultSegments.length) {
    const first = run.resultSegments.find(s => s?.content && String(s.content).trim());
    if (first) joined = String(first.content).replace(/\s+/g, ' ').trim();
  }
  if (!joined && run?.resultText) joined = String(run.resultText).replace(/\s+/g, ' ').trim();
  if (!joined) return '';
  if (joined.length <= maxChars) return joined;
  return `${joined.slice(0, maxChars - 1)}\u2026`;
}

// ---------------------------------------------------------------------------
// Credit cost estimation — mirrors backend parameter pricing enough for a
// snappy fallback before POST /json/tools/:slug/calculate-credits returns.
// ---------------------------------------------------------------------------

const OPENAI_GPT_IMAGE_SLUGS = new Set(['gpt-image-1', 'gpt-image-1-mini', 'gpt-image-2', 'gpt-image-2.5-sunburst', 'gpt-image-2.5-flare', 'gpt-image-2.6-sunburst', 'gpt-image-2.6-flare']);
const ARBITRARY_IMAGE_SIZE_SLUGS = new Set(['gpt-image-2', 'gpt-image-2.5-sunburst', 'gpt-image-2.5-flare', 'gpt-image-2.6-sunburst', 'gpt-image-2.6-flare']);

// Keep in sync with backend/inferencing/utils/image_model_parameters.js
const OPENAI_GPT_ASPECT_RATIO_TO_SIZE = {
  '1:1': '1024x1024',
  '9:16': '1024x1536',
  '3:4': '1024x1536',
  '4:5': '1024x1536',
  '2:3': '1024x1536',
  '16:9': '1536x1024',
  '4:3': '1536x1024',
  '3:2': '1536x1024',
  '21:9': '1536x1024',
};

const ARBITRARY_SIZE_LONG_EDGE = 1536;
const ARBITRARY_SIZE_DIMENSION_STEP = 16;
const ARBITRARY_SIZE_MAX_RATIO = 3;

function getArbitraryImageSizeForAspectRatio(aspectRatio) {
  const match = String(aspectRatio || '').trim().match(/^(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)$/);
  if (!match) return '';
  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!width || !height) return '';
  const clamped = Math.min(
    ARBITRARY_SIZE_MAX_RATIO,
    Math.max(1 / ARBITRARY_SIZE_MAX_RATIO, width / height),
  );
  if (clamped === 1) return '1024x1024';
  const snap = (value) => Math.max(
    ARBITRARY_SIZE_DIMENSION_STEP,
    Math.round(value / ARBITRARY_SIZE_DIMENSION_STEP) * ARBITRARY_SIZE_DIMENSION_STEP,
  );
  return clamped > 1
    ? `${ARBITRARY_SIZE_LONG_EDGE}x${snap(ARBITRARY_SIZE_LONG_EDGE / clamped)}`
    : `${snap(ARBITRARY_SIZE_LONG_EDGE * clamped)}x${ARBITRARY_SIZE_LONG_EDGE}`;
}

function deriveOpenAIGptImageSizeParam(params, modelSlug) {
  if (!params || typeof params !== 'object') return params || {};
  if (!modelSlug || !OPENAI_GPT_IMAGE_SLUGS.has(modelSlug)) return params;

  const ar = String(params.aspect_ratio ?? params.aspectRatio ?? '').trim();
  const size = ARBITRARY_IMAGE_SIZE_SLUGS.has(modelSlug)
    ? getArbitraryImageSizeForAspectRatio(ar)
    : OPENAI_GPT_ASPECT_RATIO_TO_SIZE[ar];
  if (!size) return params;
  return { ...params, size };
}

function mirrorVideoDurationAliases(params) {
  if (!params || typeof params !== 'object') return params || {};
  const value = params.seconds ?? params.duration;
  if (value === undefined || value === null || value === '') return params;
  const str = String(value);
  return { ...params, seconds: str, duration: str };
}

function estimateModelParameterCost(paramDef, paramValue) {
  const credits = paramDef?.credits || {};
  let cost = Number(credits.baseCost) || 0;

  if (paramDef?.validation?.options && credits.baseCost === 0) {
    const option = paramDef.validation.options.find(opt => opt.value === paramValue);
    if (option?.credits) {
      cost = Number(option.credits.baseCost) || 0;
      cost *= Number(option.credits.multiplier) || 1;
      if (option.credits.discountCredits) {
        cost = Math.max(0, cost - Number(option.credits.discountCredits));
      }
    }
  }

  return Math.max(0, cost);
}

/**
 * Best-effort credit estimate for a tool run before the backend responds.
 * Includes model base cost plus bound/default model-parameter option costs
 * (e.g. video duration tiers).
 */
export function buildEstimatedToolRunParams(model, values = {}, fields = []) {
  const params = {};
  for (const f of fields || []) {
    const target = f?.bindToModelParameter;
    if (!target) continue;
    const v = values?.[f.name];
    if (v === undefined || v === null || v === '') continue;
    params[target] = v;
  }

  for (const paramDef of model?.parameters || []) {
    if (params[paramDef.name] !== undefined) continue;
    const defaultValue = paramDef.value ?? paramDef.validation?.default;
    if (defaultValue !== undefined && defaultValue !== null) {
      params[paramDef.name] = defaultValue;
    }
  }

  const mirrored = mirrorVideoDurationAliases(params);
  return deriveOpenAIGptImageSizeParam(mirrored, model?.slug);
}

export function estimateToolRunCreditCost(model, values = {}, fields = []) {
  const cc = model?.creditCosts;
  if (!cc?.isEnabled) return 0;

  let total = Number(cc.baseCost) || 0;
  if (total <= 0 && !(model?.parameters || []).length) return 0;

  const params = buildEstimatedToolRunParams(model, values, fields);

  for (const paramDef of model?.parameters || []) {
    if (params[paramDef.name] === undefined) continue;
    total += estimateModelParameterCost(paramDef, params[paramDef.name]);
  }

  return Math.max(0, total);
}

// ---------------------------------------------------------------------------
// Tool model picker — curated per-tool list (default + suggested), with a
// capability-filtered catalog merge for multi-model tools (model comparison).
// ---------------------------------------------------------------------------

/** Normalize a model ref to a stable string id. */
function normalizeModelId(model) {
  const id = model?.id || model?._id;
  return id ? String(id) : null;
}

/**
 * Multi-model tools without a slug allowlist (AI Model Comparison) should
 * list every matching text/capability model, not just the curated handful.
 */
export function toolUsesCapabilityCatalogPicker(tool) {
  if (!tool?.flags?.supportsMultipleModels) return false;
  const slugs = tool?.modelFilter?.slugs;
  if (Array.isArray(slugs) && slugs.length > 0) return false;
  return true;
}

/**
 * Models the user may pick for a tool run. Uses the tool's curated
 * `defaultModel` + `suggestedModels` only — never the global catalog.
 * Disabled/archived models are omitted so the picker matches what the backend accepts.
 */
export function isToolModelDisabled(model) {
  if (!model) return true;
  if (model.flags?.isEnabled === false) return true;
  if (model.flags?.isDeprecated) return true;
  if (model.deletedAt) return true;
  if (model.archivedAt) return true;
  return false;
}

export function getToolPickerModels(tool) {
  if (!tool) return [];

  const seen = new Set();
  const out = [];

  const add = (model) => {
    if (isToolModelDisabled(model)) return;
    const id = normalizeModelId(model);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(model?.id ? model : { ...model, id });
  };

  if (tool.defaultModel) add(tool.defaultModel);
  for (const model of tool.suggestedModels || []) add(model);

  return out;
}

/**
 * Preferred model for the tool form's initial selection.
 * Music generator defaults to Music 2.6 (supports blank-lyrics instrumentals);
 * Music 2.0 cannot generate true instrumentals without a lyrics string.
 */
export function getPreferredToolModel(tool) {
  const pool = getToolPickerModels(tool);
  if (!pool.length) return tool?.defaultModel || null;
  if (tool?.slug === 'music-generator') {
    const music26 = pool.find((m) => m?.slug === 'music-2-6');
    if (music26) return music26;
  }
  return pool[0] || tool?.defaultModel || null;
}

/**
 * Return the model id the backend will actually execute for the supplied
 * values. Blank-lyrics Music 2.0 runs are upgraded to Music 2.6; every other
 * tool/model combination preserves the requested id.
 */
export function getCompatibleToolModelId(tool, values, requestedId) {
  const normalizedId = requestedId ? String(requestedId) : null;
  if (tool?.slug !== 'music-generator' || String(values?.lyrics || '').trim()) {
    return normalizedId;
  }

  const requested = normalizedId
    ? getToolPickerModels(tool).find(model => String(model?.id) === normalizedId)
    : null;
  if (normalizedId && requested?.slug !== 'music-2-0') return normalizedId;

  const preferred = getPreferredToolModel(tool);
  return preferred?.slug === 'music-2-6' && preferred?.id
    ? String(preferred.id)
    : normalizedId;
}

/** Dotted capability path used to filter the global catalog for a tool/prompt run. */
export function getToolModelCapabilityPath(tool) {
  const explicit = tool?.modelFilter?.capability;
  if (typeof explicit === 'string' && explicit.trim()) return explicit.trim();
  const byOutputType = {
    text: 'capabilities.output.hasText',
    code: 'capabilities.output.hasCode',
    document: 'capabilities.output.hasText',
    image: 'capabilities.output.hasImages',
    video: 'capabilities.output.hasVideo',
    music: 'capabilities.output.hasMusic',
    '3d-model': 'capabilities.output.has3D',
  };
  return byOutputType[tool?.outputType] || null;
}

function readModelCapability(model, path) {
  if (!model || !path) return false;
  return path.split('.').reduce((acc, key) => acc?.[key], model) === true;
}

// Mirrors backend/utils/model_capability_picker.js — keep catalog fallbacks
// from surfacing bg removers, upscalers, and other narrow post-processors.
const MEDIA_TAG_BY_PIPELINE = {
  'text|image': 'image-generators',
  'image|image': 'image-editing',
  'text|video': 'video-generators',
  'image|video': 'video-generators',
};

const EXCLUDED_MODEL_TAG_SLUGS = ['upscaling'];

function resolveToolPipelineKey(tool) {
  const byIntent = {
    'create-image': 'text|image',
    'edit-image': 'image|image',
    'create-video': 'text|video',
    'image-to-video': 'image|video',
  };
  if (tool?.intentAction && byIntent[tool.intentAction]) return byIntent[tool.intentAction];
  if (tool?.inputType && tool?.outputType) {
    const key = `${tool.inputType}|${tool.outputType}`;
    if (MEDIA_TAG_BY_PIPELINE[key]) return key;
  }
  return null;
}

function modelRequiresImageInput(model) {
  return (model?.parameters || []).some(
    (p) => p?.validation?.required && p?.validation?.file?.fileType === 'image',
  );
}

function modelHasExcludedTag(model) {
  return (model?.tags || []).some((t) => EXCLUDED_MODEL_TAG_SLUGS.includes(t?.slug));
}

function modelMatchesPipeline(model, pipelineKey) {
  if (!pipelineKey) return true;
  const requiredTag = MEDIA_TAG_BY_PIPELINE[pipelineKey];
  const tagSlugs = (model?.tags || []).map((t) => t?.slug).filter(Boolean);
  if (requiredTag && !tagSlugs.includes(requiredTag)) return false;
  if (modelHasExcludedTag(model)) return false;
  switch (pipelineKey) {
    case 'text|image':
    case 'image|image':
      return model?.capabilities?.input?.hidesTextInput !== true;
    case 'image|video':
      return model?.capabilities?.input?.hasImageSupport === true || modelRequiresImageInput(model);
    case 'text|video':
      return !modelRequiresImageInput(model);
    default:
      return true;
  }
}

function sortModelsFeaturedThenNewest(a, b) {
  const af = a?.flags?.isFeatured ? 1 : 0;
  const bf = b?.flags?.isFeatured ? 1 : 0;
  if (af !== bf) return bf - af;
  return new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime();
}

/**
 * When a tool/prompt has no curated `defaultModel` / `suggestedModels`, fall
 * back to active models from the global catalog that match `modelFilter` /
 * `outputType` — mirrors backend `resolveToolModel` step 4 and the capability
 * picker tag rules.
 */
export function getFallbackToolPickerModels(tool, modelStoreState) {
  if (!tool) return [];

  const allowedSlugs = Array.isArray(tool?.modelFilter?.slugs)
    ? tool.modelFilter.slugs.filter(Boolean)
    : [];
  const capabilityPath = getToolModelCapabilityPath(tool);
  const pipelineKey = resolveToolPipelineKey(tool);
  const models = modelStoreState?.models || {};
  const ids = [
    ...(modelStoreState?.allModelIds || []),
    ...(modelStoreState?.featuredModelIds || []),
    ...Object.keys(models),
  ];

  const seen = new Set();
  const out = [];
  for (const id of ids) {
    const m = models[id];
    if (!m || isToolModelDisabled(m)) continue;
    if (allowedSlugs.length && !allowedSlugs.includes(m.slug)) continue;
    if (capabilityPath && !readModelCapability(m, capabilityPath)) continue;
    if (capabilityPath === 'capabilities.output.hasText') {
      if (m?.capabilities?.input?.hidesTextInput === true) continue;
      if (m?.capabilities?.input?.hasTextSupport === false) continue;
    }
    if (!modelMatchesPipeline(m, pipelineKey)) continue;
    const nid = normalizeModelId(m);
    if (!nid || seen.has(nid)) continue;
    seen.add(nid);
    out.push(m?.id ? m : { ...m, id: nid });
  }
  out.sort(sortModelsFeaturedThenNewest);
  return out;
}

function mergePickerModels(primary, extra) {
  const seen = new Set();
  const out = [];
  for (const model of [...(primary || []), ...(extra || [])]) {
    const id = normalizeModelId(model);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(model);
  }
  return out;
}

/** Curated picker models, or modality-filtered catalog fallback when empty.
 * Multi-model comparison tools merge the catalog in so every text model appears. */
export function resolveToolPickerModels(tool, modelStoreState) {
  const curated = getToolPickerModels(tool);
  const catalog = getFallbackToolPickerModels(tool, modelStoreState);
  if (toolUsesCapabilityCatalogPicker(tool)) {
    if (curated.length && catalog.length) return mergePickerModels(curated, catalog);
    return curated.length ? curated : catalog;
  }
  if (curated.length) return curated;
  return catalog;
}

/** Match ModelSelectorDetailed gating for tool-scoped picks. */
export function isToolModelPlusLocked(model, userOrCanAccess) {
  // Legacy boolean: "can use all gated models" (Plus or paid packs)
  if (typeof userOrCanAccess === 'boolean') {
    if (userOrCanAccess) return false;
    const isVideoModel = !!model?.capabilities?.output?.hasVideo;
    // Free users may still use image-only models
    if (model?.capabilities?.output?.hasImages && !isVideoModel) return false;
    return !!(model?.flags?.isPlusOnly || isVideoModel);
  }
  return isModelLockedForUser(userOrCanAccess, model);
}

/** Plain text for clipboard: multi-model runs join segment bodies; else `resultText`. */
export function toolRunPlainTextForCopy(run) {
  if (Array.isArray(run?.resultSegments) && run.resultSegments.length) {
    return run.resultSegments
      .map(s => (s && typeof s.content === 'string' ? s.content.trim() : ''))
      .filter(Boolean)
      .join('\n\n---\n\n');
  }
  return run?.resultText ? String(run.resultText) : '';
}

/** Short preview for list rows (history sidebar, etc.). */
export function toolRunResultPreviewSnippet(run, maxLen = 80) {
  if (Array.isArray(run?.resultSegments) && run.resultSegments.length) {
    const first = run.resultSegments.find(s => s?.content && String(s.content).trim());
    if (first?.content) {
      const s = String(first.content).replace(/\s+/g, ' ').trim();
      return s.length <= maxLen ? s : `${s.slice(0, maxLen - 1)}\u2026`;
    }
  }
  if (run?.resultText) {
    const s = String(run.resultText).replace(/\s+/g, ' ').trim();
    return s.length <= maxLen ? s : `${s.slice(0, maxLen - 1)}\u2026`;
  }
  return '';
}
